'use client';

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { materialsService } from '@/features/design/render';
import type { Material } from '@/types/material';

// Global cache for materials data (locale-aware)
let materialsCache: {
  data: Material[] | null;
  timestamp: number;
  locale: string | null;
  promise: Promise<Material[]> | null;
} = {
  data: null,
  timestamp: 0,
  locale: null,
  promise: null,
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useMaterials() {
  const locale = useLocale();
  const [materials, setMaterials] = useState<Material[]>(
    materialsCache.data || []
  );
  const [isLoading, setIsLoading] = useState(!materialsCache.data);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMaterials = async () => {
      // Check if cache is still valid and locale matches
      const now = Date.now();
      if (
        materialsCache.data &&
        materialsCache.locale === locale &&
        now - materialsCache.timestamp < CACHE_DURATION
      ) {
        setMaterials(materialsCache.data);
        setIsLoading(false);
        return;
      }

      // If there's already a fetch in progress, wait for it
      if (materialsCache.promise) {
        try {
          setIsLoading(true);
          const response = await materialsCache.promise;
          setMaterials(response);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to fetch materials'
          );
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Make new API call
      try {
        setIsLoading(true);
        materialsCache.promise = materialsService.getMaterials();
        const response = await materialsCache.promise;

        // Update cache
        materialsCache = {
          data: response,
          timestamp: now,
          locale,
          promise: null,
        };

        setMaterials(response);
      } catch (err) {
        materialsCache.promise = null;
        setError(
          err instanceof Error ? err.message : 'Failed to fetch materials'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaterials();
  }, [locale]);

  return { materials, isLoading, error };
}
